/**
 * Client-side video compression using canvas + MediaRecorder (no external lib).
 *
 * Note: runs at video playback speed (i.e., a 60-min clip takes ~60 min to compress).
 * Best used for CCTV clips of a few minutes each rather than multi-hour files.
 *
 * Output is WebM (VP9). Backend/ffmpeg handle any video/* mime type.
 */

export const QUALITY_PRESETS = {
  original: null,
  fhd:  { label: "1080p · High",   maxHeight: 1080, videoBps: 4_000_000, audioBps: 128_000 },
  hd:   { label: "720p · Medium",  maxHeight: 720,  videoBps: 2_000_000, audioBps: 96_000 },
  sd:   { label: "480p · Low",     maxHeight: 480,  videoBps: 800_000,   audioBps: 64_000 },
};

function pickMime() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const c of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
  }
  return "video/webm";
}

/**
 * Compress a video File/Blob to a smaller Blob using a canvas-based pipeline.
 * @param {File} file
 * @param {{maxHeight:number, videoBps:number, audioBps:number}} preset
 * @param {(pct:number)=>void} onProgress — 0..100
 * @returns {Promise<Blob>}
 */
export async function compressVideoClientSide(file, preset, onProgress) {
  const src = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;   // required for autoplay
  video.playsInline = true;
  video.src = src;
  video.preload = "metadata";

  await new Promise((res, rej) => {
    video.onloadedmetadata = res;
    video.onerror = () => rej(new Error("Cannot decode source video"));
  });

  const srcW = video.videoWidth;
  const srcH = video.videoHeight;
  const duration = video.duration || 0;

  // Downscale keeping aspect ratio
  const scale = Math.min(1, preset.maxHeight / srcH);
  const outW = Math.max(2, Math.round(srcW * scale) & ~1); // even numbers only
  const outH = Math.max(2, Math.round(srcH * scale) & ~1);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");

  const videoStream = canvas.captureStream(25); // 25 fps re-encode

  // Capture audio from the source element if possible
  let audioTrack = null;
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const ac = new AudioCtx();
      const dest = ac.createMediaStreamDestination();
      const source = ac.createMediaElementSource(video);
      source.connect(dest);
      // We still want the user to be able to record — do NOT connect to ac.destination
      audioTrack = dest.stream.getAudioTracks()[0];
      if (audioTrack) videoStream.addTrack(audioTrack);
    }
  } catch (_) { /* audio capture optional; continue without it */ }

  const mime = pickMime();
  const recorder = new MediaRecorder(videoStream, {
    mimeType: mime,
    videoBitsPerSecond: preset.videoBps,
    audioBitsPerSecond: preset.audioBps,
  });

  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };

  let stopped = false;
  const done = new Promise((res) => { recorder.onstop = () => { stopped = true; res(); }; });

  recorder.start(500);

  // Frame pump
  let raf = 0;
  const draw = () => {
    if (video.paused || video.ended) return;
    ctx.drawImage(video, 0, 0, outW, outH);
    if (duration > 0 && onProgress) {
      const pct = Math.min(99, (video.currentTime / duration) * 100);
      onProgress(Math.round(pct));
    }
    raf = requestAnimationFrame(draw);
  };

  const playPromise = video.play();
  if (playPromise?.catch) playPromise.catch(() => { /* autoplay may block; muted:true should bypass */ });
  raf = requestAnimationFrame(draw);

  await new Promise((res) => {
    video.onended = res;
    video.onerror = res;
  });

  cancelAnimationFrame(raf);
  if (recorder.state !== "inactive") recorder.stop();
  if (!stopped) await done;

  URL.revokeObjectURL(src);
  onProgress && onProgress(100);
  return new Blob(chunks, { type: mime.split(";")[0] });
}

/** Rough estimate of the compressed output size given source duration + bitrate. */
export function estimateSize(durationSec, preset) {
  if (!preset) return null;
  return Math.round((preset.videoBps + preset.audioBps) / 8 * durationSec);
}

export function formatBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 ? 2 : 1)} ${u[i]}`;
}
